import { Check, Edit01, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import CheckBox from 'src/components/CheckBox';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { usePhone as usePhoneCommon } from 'src/utils/CommonFunctions';

interface AddressType {
    type: 'shipper' | 'consignee' | 'both';
    label: string;
    style: 'blue' | 'warning' | 'success';
}

interface AdditionalService {
    id: string;
    label: string;
    type?: 'PICKUP' | 'DELIVERY' | 'OTHER';
}

interface AddressListRowProps {
    data: any;
    onEdit?: (addressId: string) => void;
    onDelete?: (address: any) => void;
    getAdditionalServices: (address: any) => AdditionalService[];
    getAddressTypes: (address: any) => AddressType[];
    countryFlagFn?: (code: string) => string;
    headCells?: any[];
    selectedAddressIds?: string[];
    onToggleSelect?: (id: string) => void;
}

const ServiceItem = ({ service }: { service: string }) => {
    const ref = useRef<HTMLSpanElement>(null);

    return (
        <TruncateTextCmp message={service} refName={ref} tooltipType="action">
            <span className="text-xs text-textSecondary font-medium leading-[18px] block truncate" ref={ref}>
                {service}
            </span>
        </TruncateTextCmp>
    );
};

const AddressListRow: React.FC<AddressListRowProps> = ({ data, onEdit, onDelete, getAdditionalServices, getAddressTypes, headCells, selectedAddressIds, onToggleSelect }) => {
    const { t } = useTranslation();
    const nameRef = useRef<HTMLParagraphElement>(null);
    const addressRef = useRef<HTMLParagraphElement>(null);
    const contactNameRef = useRef<HTMLParagraphElement>(null);
    const { getFormatPhoneWithPlus } = usePhoneCommon();
    const [isServicesOpen, setIsServicesOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const servicesCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const handleCloseOthers = (e: Event) => {
            if ((e as CustomEvent).detail !== data?.id) {
                setIsServicesOpen(false);
            }
        };
        document.addEventListener('address-row-services-open', handleCloseOthers);

        return () => {
            document.removeEventListener('address-row-services-open', handleCloseOthers);
            if (servicesCloseTimerRef.current) clearTimeout(servicesCloseTimerRef.current);
        };
    }, [data?.id]);

    const cancelCloseServices = () => {
        if (servicesCloseTimerRef.current) {
            clearTimeout(servicesCloseTimerRef.current);
            servicesCloseTimerRef.current = null;
        }
    };

    const openServices = () => {
        cancelCloseServices();

        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const goUp = spaceBelow < 260;
            setOpenUpward(goUp);
            setDropdownPos({
                top: goUp ? rect.top + window.scrollY : rect.bottom + window.scrollY,
                left: rect.left + rect.width / 2 + window.scrollX,
            });
        }
        document.dispatchEvent(new CustomEvent('address-row-services-open', { detail: data?.id }));
        setIsServicesOpen(true);
    };

    const scheduleCloseServices = () => {
        cancelCloseServices();
        servicesCloseTimerRef.current = setTimeout(() => setIsServicesOpen(false), 150);
    };

    const selectedSet = useMemo(() => new Set(selectedAddressIds ?? []), [selectedAddressIds]);

    const addressTypes = getAddressTypes(data);
    const additionalServices = getAdditionalServices(data);

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit?.(data?.id);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete?.(data);
    };

    // Map column id → JSX for dynamic column ordering + visibility (mirrors NewOrderRow pattern)
    const tableCells = useMemo(
        () => [
            {
                key: 'companyName',
                htmlDynamic: (
                    <td className="max-w-px min-w-[170px]">
                        <TruncateTextCmp message={data?.companyName} refName={nameRef} tooltipType="action">
                            <p className="text-grayLight900 text-[12px] leading-[18px] block font-medium truncate" ref={nameRef}>
                                {data?.companyName || '-'}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'addressType',
                htmlDynamic: (
                    <td className="max-w-[9.15%] min-w-[140px]">
                        <div className="flex items-center gap-x-1 flex-wrap">
                            {addressTypes.map((type: AddressType, index: number) => (
                                <BadgeCmp key={index} style="modern" type={type.style}>
                                    {type.label}
                                </BadgeCmp>
                            ))}
                        </div>
                    </td>
                ),
            },
            {
                key: 'fullAddress',
                htmlDynamic: (
                    <td className="max-w-px min-w-[270px]">
                        <TruncateTextCmp message={data?.fullAddress} refName={addressRef} tooltipType="action" truncateTextParentClassName="">
                            <p className="text-grayLight600 font-normal truncate" ref={addressRef}>
                                {data?.fullAddress || '-'}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'contactName',
                htmlDynamic: (
                    <td className="max-w-px min-w-[170px]">
                        <TruncateTextCmp message={data?.contactName} refName={contactNameRef} tooltipType="action">
                            <p className="text-grayLight600 font-normal truncate" ref={contactNameRef}>
                                {data?.contactName || '-'}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'contactNumber',
                htmlDynamic: (
                    <td className="max-w-[9.15%] min-w-[170px]">
                        <div className="flex items-center gap-x-1.5">
                            <span className="text-grayLight600 font-normal text-nowrap">{data?.contactNumber ? getFormatPhoneWithPlus(data.contactNumber) : '-'}</span>
                        </div>
                    </td>
                ),
            },
            {
                key: 'additionalServices',
                htmlDynamic: (
                    <td className="max-w-[9.15%] min-w-[160px]">
                        {additionalServices.length > 0 ? (
                            <OutsideClickHandler onOutsideClick={() => setIsServicesOpen(false)} containerClassName="inline-block">
                                <div
                                    ref={triggerRef}
                                    className="flex items-center gap-x-1 cursor-pointer"
                                    onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        openServices();
                                    }}
                                    onMouseLeave={scheduleCloseServices}
                                >
                                    <BadgeCmp style="modern" type="gray" isHidePillDot mainClassName="cursor-pointer">
                                        <span>{additionalServices.length}</span>
                                    </BadgeCmp>
                                </div>
                                {isServicesOpen &&
                                    ReactDOM.createPortal(
                                        <div
                                            style={{
                                                position: 'fixed',
                                                top: openUpward ? undefined : dropdownPos.top,
                                                bottom: openUpward ? window.innerHeight - dropdownPos.top + 6 : undefined,
                                                left: dropdownPos.left,
                                                transform: 'translateX(-50%)',
                                                marginTop: openUpward ? undefined : 6,
                                            }}
                                            className="z-[9999] bg-white border border-utilityGray200 rounded-lg shadow px-2 py-2 min-w-[244px]"
                                            onMouseEnter={cancelCloseServices}
                                            onMouseLeave={scheduleCloseServices}
                                        >
                                            <div className="grid grid-cols-1 gap-y-2 max-h-[200px] overflow-y-auto custom-scrollbar-v2 border border-utilityGray200 rounded-md p-3">
                                                {additionalServices
                                                    .map((s) => s.label)
                                                    .filter(Boolean)
                                                    .map((service) => (
                                                        <div key={service} className="flex items-center gap-x-1.5 min-w-0">
                                                            <Check className="w-4 min-w-4 h-4 min-h-4 text-primary flex-none" />
                                                            <ServiceItem service={service} />
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                            </OutsideClickHandler>
                        ) : (
                            <span className="text-grayLight600">-</span>
                        )}
                    </td>
                ),
            },
        ],
        [data, addressTypes, additionalServices, isServicesOpen, openUpward, t]
    );

    const actionCell = (
        <td className="min-w-[64px] w-[64px] max-w-[64px] sticky right-0 bg-inherit z-[1] table-cell-with-tooltip">
            <div className="flex items-center justify-end gap-1.5 h-full table-col-tooltip-space-common-xs">
                <TooltipCmp message={t('Common.Edit')} position="top">
                    <div className="w-4 h-4 flex items-center justify-center flex-none cursor-pointer" onClick={handleEdit}>
                        <Edit01 className="w-4 h-4 text-grayLight600" />
                    </div>
                </TooltipCmp>
                <TooltipCmp message={t('Common.Delete')} position="top">
                    <div className="w-4 h-4 flex items-center justify-center flex-none cursor-pointer" onClick={handleDelete}>
                        <Trash01 className="w-4 h-4 text-grayLight600" />
                    </div>
                </TooltipCmp>
            </div>
        </td>
    );

    // Fallback: render all cells if no headCells provided
    if (!headCells) {
        return (
            <>
                {tableCells.map((c) => (
                    <React.Fragment key={c.key}>{c.htmlDynamic}</React.Fragment>
                ))}
                {actionCell}
            </>
        );
    }

    // Render configurable columns in headCells order respecting visibility, action always last
    return (
        <>
            {headCells
                .filter((cell: any) => cell.visible)
                .map((cell: any) => {
                    if (cell.id === 'checkbox') {
                        return (
                            <React.Fragment key="checkbox">
                                <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip" onClick={(e) => e.stopPropagation()}>
                                    <CheckBox
                                        id={`select-address-${data.id}`}
                                        checked={selectedSet.has(data.id)}
                                        onChangeFunc={(e: any) => {
                                            e.stopPropagation();
                                            onToggleSelect?.(data.id);
                                        }}
                                        name="selectAddress"
                                        value={String(data.id)}
                                        parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                                    />
                                </td>
                            </React.Fragment>
                        );
                    }

                    if (cell.id === 'action') {
                        return <React.Fragment key="action">{actionCell}</React.Fragment>;
                    }

                    const matched = tableCells.find((tc) => tc.key === cell.id);
                    if (!matched) return null;

                    return <React.Fragment key={cell.id}>{matched.htmlDynamic}</React.Fragment>;
                })}
        </>
    );
};

export default AddressListRow;
